import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent } from '../../../ui/Dialog';
import Button from '../../../ui/Button';
import { ClosePopup } from '../../../assets/images';
import { useLanguage } from '../../../localization/useLanguage';
import { removeMoreNumberChar } from '../../../Helpers/Chat/ContentEditableEle';
import { REACT_APP_GROUP_NAME_CHAR, REACT_APP_GROUP_DESCRIPTION_CHAR } from '../../../Components/processENV';
import SDK from '../../../Components/SDK';
import logger from '../../../Helpers/logger';
import styles from './EditChatDialog.module.scss';

function DialogHead({ title, closeLabel, onClose }) {
    return (
        <div className={styles.head}>
            <h3>{title}</h3>
            <button type="button" className={styles.close} onClick={onClose} aria-label={closeLabel}>
                <ClosePopup />
            </button>
        </div>
    );
}

DialogHead.propTypes = {
    title: PropTypes.string,
    closeLabel: PropTypes.string,
    onClose: PropTypes.func.isRequired,
};

function NameField({ inputRef, value, onChange, onKeyDown, disabled, hasError }) {
    const { t } = useLanguage();
    return (
        <div className={`${styles.field} ${hasError ? styles.error : ''}`}>
            <label htmlFor="edit-chat-name">
                {t('chat.menu.EDIT_CHAT_NAME_LABEL')} <span aria-hidden>*</span>
            </label>
            <div className={styles.control}>
                <input
                    id="edit-chat-name"
                    ref={inputRef}
                    value={value}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    disabled={disabled}
                    placeholder={t('chat.menu.EDIT_CHAT_NAME_PLACEHOLDER')}
                    aria-invalid={hasError}
                    aria-describedby={hasError ? 'edit-chat-name-error' : undefined}
                    autoFocus
                />
            </div>
            {hasError && (
                <p className={styles.message} id="edit-chat-name-error">
                    {t('chat.menu.EDIT_CHAT_NAME_REQUIRED')}
                </p>
            )}
        </div>
    );
}

NameField.propTypes = {
    inputRef: PropTypes.object,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    hasError: PropTypes.bool,
};

function DescriptionField({ value, onChange, disabled }) {
    const { t } = useLanguage();
    return (
        <div className={styles.field}>
            <label htmlFor="edit-chat-description">{t('chat.menu.EDIT_CHAT_DESC_LABEL')}</label>
            <div className={styles.textarea}>
                <textarea
                    id="edit-chat-description"
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    placeholder={t('chat.menu.EDIT_CHAT_DESC_PLACEHOLDER')}
                />
                <span className={styles.count}>
                    {value.length}/{REACT_APP_GROUP_DESCRIPTION_CHAR}
                </span>
            </div>
        </div>
    );
}

DescriptionField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

/**
 * Loads a group's description, which the group list the store is built from omits.
 *
 * @param groupJid Group to load.
 * @returns The editable value, its setter, and the value the server returned —
 *          null until it arrives, so callers can hold back a save that would
 *          otherwise write the still-empty box over a real description.
 */
function useGroupDescription(groupJid) {
    const [description, setDescription] = useState('');
    const [loaded, setLoaded] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const res = await SDK.getGroupProfile(groupJid);
                if (cancelled) return;
                if (res?.statusCode === 200) {
                    const current = res.data?.groupDescription || '';
                    setDescription(current);
                    setLoaded(current);
                }
            } catch (error) {
                logger.error('EditChatDialog description fetch failed', error);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [groupJid]);

    return { description, setDescription, loaded };
}

/**
 * Rename a group and edit its description.
 *
 * @param groupJid Group to load the description for.
 * @param onSave Resolves true once the server confirms; false keeps the dialog
 *               open with the typed text intact so the user can retry.
 */
export default function EditChatDialog({ open, onOpenChange, groupJid, initialName, onSave }) {
    const { t } = useLanguage();
    const [name, setName] = useState(initialName);
    const { description, setDescription, loaded: loadedDescription } = useGroupDescription(groupJid);
    const [nameError, setNameError] = useState(false);
    const [saving, setSaving] = useState(false);
    const nameRef = useRef(null);

    const trimmedName = name.trim();
    const trimmedDescription = description.trim();
    const isDescriptionReady = loadedDescription !== null;
    const isUnchanged = trimmedName === initialName.trim() && trimmedDescription === (loadedDescription || '').trim();

    const handleName = event => {
        setName(removeMoreNumberChar(REACT_APP_GROUP_NAME_CHAR, event.target.value));
        if (event.target.value.trim()) setNameError(false);
    };

    const handleDescription = event => {
        setDescription(removeMoreNumberChar(REACT_APP_GROUP_DESCRIPTION_CHAR, event.target.value));
    };

    // Guarded rather than relying on the disabled attribute alone, which a fast
    // double Enter can outrun.
    const handleSave = async () => {
        if (saving || !isDescriptionReady) return;
        if (!trimmedName) {
            setNameError(true);
            nameRef.current?.focus();
            return;
        }
        // Nothing to send, so skip the round-trip and the success toast with it.
        if (isUnchanged) {
            onOpenChange(false);
            return;
        }

        setSaving(true);
        const saved = await onSave({ name: trimmedName, description: trimmedDescription });
        setSaving(false);
        if (saved) onOpenChange(false);
    };

    // Bound to the name field only so Enter still inserts newlines in the description.
    const handleNameKeyDown = event => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSave();
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className={styles.container}>
                <DialogHead
                    title={t('chat.menu.EDIT_CHAT')}
                    closeLabel={t('common.CLOSE')}
                    onClose={() => onOpenChange(false)}
                />

                <NameField
                    inputRef={nameRef}
                    value={name}
                    onChange={handleName}
                    onKeyDown={handleNameKeyDown}
                    disabled={saving}
                    hasError={nameError}
                />

                <DescriptionField
                    value={description}
                    onChange={handleDescription}
                    disabled={saving || !isDescriptionReady}
                />

                <div className={styles.actions}>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving || !trimmedName || !isDescriptionReady}
                    >
                        {t('chat.menu.EDIT_CHAT_SAVE')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

EditChatDialog.propTypes = {
    open: PropTypes.bool,
    onOpenChange: PropTypes.func.isRequired,
    groupJid: PropTypes.string.isRequired,
    initialName: PropTypes.string,
    onSave: PropTypes.func.isRequired,
};

EditChatDialog.defaultProps = {
    initialName: '',
};
